@file:kotlin.js.JsExport
package com.narbase.letsgo.web.dto.voiceassistant

import com.narbase.letsgo.dto.DtoName
import com.narbase.letsgo.dto.KmmLong
import com.narbase.letsgo.web.dto.locationpoint.LocationPointDto
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantConfidence
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantEngine
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantField
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantEngineDecisionConfidence
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantOutcome
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantMediumConfidenceAction
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantModelProvider
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantSpeechDetector

class CurrentLocationDto(
    val lat: Double,
    val lng: Double,
    val accuracyMeters: Double,
)

class VoiceAssistantCandidateDto(
    val locationPoint: LocationPointDto,
    val title: String,
    val subtitle: String?,
    val matchScore: Double,
)

class VoiceAssistantFieldInterpretationDto(
    @Suppress("NON_EXPORTABLE_TYPE") val field: DtoName<VoiceAssistantField>,
    @Suppress("NON_EXPORTABLE_TYPE") val confidence: DtoName<VoiceAssistantConfidence>,
    val candidates: Array<VoiceAssistantCandidateDto>,
)

class VoiceAssistantInterpretationDto(
    val sessionId: String,
    val transcript: String,
    @Suppress("NON_EXPORTABLE_TYPE") val outcome: DtoName<VoiceAssistantOutcome>,
    val fieldInterpretations: Array<VoiceAssistantFieldInterpretationDto>,
    val assistantMessage: String?,
)

data class VoiceAssistantTranscriptionFallbackDto(
    @Suppress("NON_EXPORTABLE_TYPE") val provider: DtoName<VoiceAssistantModelProvider>,
    val baseUrl: String,
    val model: String,
    val apiKey: String,
    val hasApiKey: Boolean,
    val isEnabled: Boolean,
)

data class VoiceAssistantSettingsDto(
    val isEnabled: Boolean,
    @Suppress("NON_EXPORTABLE_TYPE") val engine: DtoName<VoiceAssistantEngine>,
    val aiStandaloneBaseUrl: String,
    val aiStandaloneApiKey: String,
    val hasAiStandaloneApiKey: Boolean,
    val aiStandaloneHighConfidenceScore: Double,
    val aiStandaloneMediumConfidenceScore: Double,
    val aiStandaloneLowConfidenceScore: Double,
    @Suppress("NON_EXPORTABLE_TYPE") val aiStandaloneMediumConfidenceAction: DtoName<VoiceAssistantMediumConfidenceAction>,
    @Suppress("NON_EXPORTABLE_TYPE") val aiStandaloneLowConfidenceAction: DtoName<VoiceAssistantMediumConfidenceAction>,
    @Suppress("NON_EXPORTABLE_TYPE") val aiStandaloneConfirmConfidence: DtoName<VoiceAssistantEngineDecisionConfidence>,
    @Suppress("NON_EXPORTABLE_TYPE") val aiStandaloneHandoffConfidence: DtoName<VoiceAssistantEngineDecisionConfidence>,
    val enabledPassengerProfileGroupIds: Array<Int>,
    val introAudioUrl: String?,
    val maxRecordingSeconds: Int,
    @Suppress("NON_EXPORTABLE_TYPE") val speechDetector: DtoName<VoiceAssistantSpeechDetector>,
    val isSendOnSilenceEnabled: Boolean,
    val silenceSecondsBeforeSend: Int,
    val isRecordingTimerStartedOnSpeech: Boolean,
    val isSilencedMicrophoneDetected: Boolean,
    val isRouteConfirmationSkippedOnHighConfidence: Boolean,
    val isRideBookedOnRouteConfirmation: Boolean,
    val isOnlyDestinationInterpreted: Boolean,
    val isAiStandaloneHintUsed: Boolean,
    @Suppress("NON_EXPORTABLE_TYPE") val mediumConfidenceAction: DtoName<VoiceAssistantMediumConfidenceAction>,
    @Suppress("NON_EXPORTABLE_TYPE") val lowConfidenceAction: DtoName<VoiceAssistantMediumConfidenceAction>,
    val candidatesCount: Int,
    val searchResultsCount: Int,
    @Suppress("NON_EXPORTABLE_TYPE") val chatProvider: DtoName<VoiceAssistantModelProvider>,
    val chatBaseUrl: String,
    val chatModel: String,
    val chatApiKey: String,
    val hasChatApiKey: Boolean,
    val chatTemperature: Double,
    val isChatThinkingEnabled: Boolean,
    val chatAnswersInSeparateStep: Boolean,
    @Suppress("NON_EXPORTABLE_TYPE") val transcriptionProvider: DtoName<VoiceAssistantModelProvider>,
    val transcriptionBaseUrl: String,
    val transcriptionModel: String,
    val transcriptionApiKey: String,
    val hasTranscriptionApiKey: Boolean,
    val transcriptionTemperature: Double,
    val transcriptionFallbacks: Array<VoiceAssistantTranscriptionFallbackDto>,
    val transcriptionLanguage: String,
    val transcriptionPrompt: String,
    val maxInterpretationsPerDay: Int,
    val maxAudioBytes: Int,
    val audioRetentionHours: Int,
    val maxResponseTokens: Int,
    val elasticsearchMcpUrl: String,
    val elasticsearchMcpToolName: String,
    val openAiRequestTimeoutSeconds: Int,
    val maxToolCallRounds: Int,
    val systemPrompt: String?,
    val dropoffRules: String?,
    val pickupRules: String?,
    val destinationOnlyRules: String?,
    val aiStandaloneHintPrompt: String?,
    val modelsSuspendedReason: String?,
)

class VoiceAssistantPromptsDto(
    val systemPrompt: String,
    val dropoffRules: String,
    val pickupRules: String,
    val destinationOnlyRules: String,
    val aiStandaloneHintPrompt: String,
)

class VoiceAssistantSearchResultLogDto(
    val candidateId: Int,
    val name: String,
    val address: String?,
    val lat: Double,
    val lng: Double,
    val area: String? = null,
    val district: String? = null,
    val relevance: Double? = null,
)

class VoiceAssistantSearchLogDto(
    val query: String,
    val results: Array<VoiceAssistantSearchResultLogDto>,
)

class VoiceAssistantTurnLogDto(
    val turnId: String,
    @Suppress("NON_EXPORTABLE_TYPE") val field: DtoName<VoiceAssistantField>,
    val transcript: String,
    @Suppress("NON_EXPORTABLE_TYPE") val confidence: DtoName<VoiceAssistantConfidence>,
    @Suppress("NON_EXPORTABLE_TYPE") val outcome: DtoName<VoiceAssistantOutcome>,
    @Suppress("NON_EXPORTABLE_TYPE") val engine: DtoName<VoiceAssistantEngine>,
    val chatModel: String?,
    val transcriptionModel: String?,
    val hasAudio: Boolean,
    val audioMimeType: String?,
    val searches: Array<VoiceAssistantSearchLogDto>,
    val modelReplies: Array<VoiceAssistantModelReplyLogDto>,
    val transcriptionUsage: VoiceAssistantTranscriptionUsageLogDto?,
    val shownResult: VoiceAssistantShownResultLogDto?,
    val failureReason: String?,
    val isRefused: Boolean,
    val durationMillis: KmmLong,
    val createdAt: KmmLong,
)

class VoiceAssistantModelReplyLogDto(
    val model: String?,
    val toolCalls: Array<VoiceAssistantToolCallLogDto>,
    val content: String?,
    val finishReason: String?,
    val promptTokens: Int?,
    val cachedPromptTokens: Int?,
    val completionTokens: Int?,
    val reasoningTokens: Int?,
    val usageJson: String?,
    val cost: Double?,
    val durationMillis: KmmLong,
)

class VoiceAssistantTranscriptionUsageLogDto(
    val inputTokens: Int?,
    val inputAudioTokens: Int?,
    val inputTextTokens: Int?,
    val outputTokens: Int?,
    val audioSeconds: Double?,
    val usageJson: String?,
    val durationMillis: KmmLong,
)

class VoiceAssistantToolCallLogDto(
    val call: String,
    val durationMillis: KmmLong,
)

class VoiceAssistantShownResultLogDto(
    @Suppress("NON_EXPORTABLE_TYPE") val outcome: DtoName<VoiceAssistantOutcome>,
    val assistantMessage: String?,
    val fieldInterpretations: Array<VoiceAssistantFieldInterpretationDto>,
)

class VoiceAssistantSessionLogDto(
    val sessionId: String,
    val passengerId: KmmLong,
    val passengerName: String?,
    @Suppress("NON_EXPORTABLE_TYPE") val outcome: DtoName<VoiceAssistantOutcome>,
    @Suppress("NON_EXPORTABLE_TYPE") val confidence: DtoName<VoiceAssistantConfidence>,
    @Suppress("NON_EXPORTABLE_TYPE") val engine: DtoName<VoiceAssistantEngine>,
    val selectedPointName: String?,
    val transferReason: String?,
    val createdAt: KmmLong,
    val totalDurationMillis: KmmLong,
    val turns: Array<VoiceAssistantTurnLogDto>,
    val rideId: KmmLong?,
    val rideStatus: String?,
)

class VoiceAssistantConnectionHealthDto(
    val isHealthy: Boolean,
    val target: String,
    val url: String,
    val latencyMillis: Int,
    val failure: String?,
)
